import { useState, useMemo } from 'react';
import { Calculator, TrendingDown, Calendar, Coins } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Debt } from '@/hooks/useDebts';
import {
  calculateSnowballProjection,
  calculateSnowballSavings,
  generateBalanceTimeline,
  formatMonthsToYearsMonths,
} from '@/lib/snowball-calculations';
import { chartColors, tooltipStyle, gridStroke, gridStrokeDasharray, axisTickStyle } from '@/lib/chart-config';

interface SnowballProjectionProps {
  debts: Debt[];
  currency?: string;
}

export function SnowballProjection({ debts, currency = 'CRC' }: SnowballProjectionProps) {
  const [extraPayment, setExtraPayment] = useState(0);

  const activeDebts = debts.filter(d => d.status === 'active');
  const projection = calculateSnowballProjection(activeDebts, extraPayment);
  const savings = calculateSnowballSavings(activeDebts, extraPayment);
  const timeline = useMemo(() => generateBalanceTimeline(activeDebts, extraPayment), [activeDebts, extraPayment]);

  const debtNames = activeDebts.map(d => d.name);
  const areaColors = [chartColors.navy, chartColors.blue, chartColors.emerald, chartColors.teal, chartColors.amber, chartColors.rose];

  const formatCurrency = (amount: number) => {
    if (amount === Infinity) return '∞';
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    if (date.getFullYear() === 9999) return 'Nunca';
    return date.toLocaleDateString('es-CR', {
      month: 'long',
      year: 'numeric',
    });
  };

  const totalMonths = Math.max(...projection.map(p => p.monthsToPayoff), 0);
  const lastPayoffDate = projection.length > 0
    ? projection[projection.length - 1]?.payoffDate
    : null;

  if (activeDebts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Proyección Bola de Nieve
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            No hay deudas activas para proyectar.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Extra Payment Input */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Calculadora de Proyección
          </CardTitle>
          <CardDescription>
            Calcula cuánto ahorrarás usando el método bola de nieve
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="extra">Pago extra mensual (opcional)</Label>
            <Input
              id="extra"
              type="number"
              min="0"
              step="100"
              value={extraPayment || ''}
              onChange={(e) => setExtraPayment(parseFloat(e.target.value) || 0)}
              placeholder="0"
            />
            <p className="text-xs text-muted-foreground">
              Agrega cualquier monto adicional que puedas destinar cada mes a pagar deudas
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Savings Summary */}
      <div className="grid gap-3 grid-cols-2">
        <Card className="bg-gradient-to-br from-success/10 to-success/5 border-success/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-success/20 rounded-lg shrink-0">
                <Coins className="h-4 w-4 text-success" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Ahorro en intereses</p>
                <p className="text-lg font-bold text-success truncate">
                  {formatCurrency(savings.interestSaved)}
                </p>
                <p className="text-xs text-success/80">
                  {savings.interestSavedPercentage.toFixed(1)}% menos
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-primary/20 rounded-lg shrink-0">
                <Calendar className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Libre de deudas en</p>
                <p className="text-base font-bold">
                  {formatMonthsToYearsMonths(totalMonths)}
                </p>
                {lastPayoffDate && (
                  <p className="text-xs text-muted-foreground">
                    {formatDate(lastPayoffDate)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-warning/20 rounded-lg shrink-0">
                <TrendingDown className="h-4 w-4 text-warning" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Interés total</p>
                <p className="text-lg font-bold truncate">
                  {formatCurrency(savings.totalInterestWithSnowball)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-accent/20 rounded-lg shrink-0">
                <Calendar className="h-4 w-4 text-accent-foreground" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Meses ahorrados</p>
                <p className="text-lg font-bold text-primary">
                  {savings.monthsSaved > 0 ? savings.monthsSaved : 0}
                </p>
                <p className="text-xs text-muted-foreground">
                  vs. pagos mínimos
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Balance Reduction Chart */}
      {timeline.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5" />
              Reducción de saldo mes a mes
            </CardTitle>
            <CardDescription>
              Así disminuirá tu deuda total con el método bola de nieve
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timeline} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid
                    strokeDasharray={gridStrokeDasharray}
                    stroke={gridStroke}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={axisTickStyle}
                    interval={Math.max(0, Math.floor(timeline.length / 8))}
                  />
                  <YAxis
                    tick={axisTickStyle}
                    tickFormatter={(v: number) =>
                      v >= 1_000_000
                        ? `${(v / 1_000_000).toFixed(1)}M`
                        : v >= 1_000
                          ? `${(v / 1_000).toFixed(0)}K`
                          : v.toString()
                    }
                    width={50}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(value: number, name: string) => [
                      new Intl.NumberFormat('es-CR', {
                        style: 'currency',
                        currency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(value),
                      name,
                    ]}
                  />
                  <Legend />
                  {debtNames.map((name, i) => (
                    <Area
                      key={name}
                      type="monotone"
                      dataKey={name}
                      stackId="1"
                      stroke={areaColors[i % areaColors.length]}
                      fill={areaColors[i % areaColors.length]}
                      fillOpacity={0.3}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}


      <Card>
        <CardHeader>
          <CardTitle>Orden de pago</CardTitle>
          <CardDescription>
            Tu plan personalizado para salir de deudas con el método bola de nieve
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {projection.map((item, index) => {
              const debt = activeDebts.find(d => d.id === item.debtId);
              if (!debt) return null;

              const progressInTimeline = (index / projection.length) * 100;

              return (
                <div key={item.debtId} className="relative">
                  {index < projection.length - 1 && (
                    <div className="absolute left-[15px] top-10 w-0.5 h-full bg-border" />
                  )}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                      {item.order}
                    </div>
                    <div className="flex-1 pb-6">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold">{debt.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            Saldo: {formatCurrency(debt.current_balance)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">
                            {formatMonthsToYearsMonths(item.monthsToPayoff)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(item.payoffDate)}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Interés a pagar: </span>
                          <span className="font-medium">
                            {formatCurrency(item.totalInterestPaid)}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Total a pagar: </span>
                          <span className="font-medium">
                            {formatCurrency(item.totalPaid)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Savings per Debt */}
      {savings.savingsPerDebt.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Ahorro por deuda</CardTitle>
            <CardDescription>
              Cuánto ahorrarás en intereses por cada deuda usando bola de nieve
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {savings.savingsPerDebt.map((item) => (
                <div key={item.debtId} className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium">{item.debtName}</span>
                    <span className="text-success font-semibold">
                      {formatCurrency(item.interestSaved)} ({item.interestSavedPercentage.toFixed(1)}%)
                    </span>
                  </div>
                  <Progress 
                    value={item.interestSavedPercentage} 
                    className="h-2"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
